import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, isRef as _isRef, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "q-py-sm bg-neutral sticky-top" }

import { computed, ref, watch } from "vue";
import { ArtifactInput, ArtifactSchema, ArtifactTypeSchema } from "@/types";
import { filterArtifacts, sortSelectedArtifactsToTop } from "@/util";
import { artifactStore, useVModel } from "@/hooks";
import { Typography, AttributeChip } from "@/components/common/display";
import { TypeButtons } from "@/components/common/button";
import ArtifactBodyDisplay from "@/components/artifact/display/ArtifactBodyDisplay.vue";


/**
 * An input for artifacts.
 */
const __default__ = {
  name: "ArtifactInput",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    modelValue: {},
    onlyDocumentArtifacts: { type: Boolean },
    hiddenArtifactIds: {},
    defaultHiddenTypes: {},
    label: {},
    multiple: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const model = useVModel(props, "modelValue");

const artifacts = computed(() =>
  props.onlyDocumentArtifacts
    ? artifactStore.currentArtifacts
    : artifactStore.allArtifacts
);
const sortedArtifacts = computed(() =>
  sortSelectedArtifactsToTop(artifacts.value, model.value)
);

const options = ref(sortedArtifacts.value);
const hiddenTypes = ref(props.defaultHiddenTypes || []);

const selectedCount = computed(() => {
  if (typeof model.value === "string") {
    return 1;
  } else if (Array.isArray(model.value)) {
    return model.value.length;
  } else {
    return 0;
  }
});

/**
 * Filters the artifact options.
 * @param searchText - The search text to filter with.
 * @param update - A function call to update the options.
 */
function filterOptions(
  searchText: string,
  update: (fn: () => void) => void
): void {
  update(() => {
    if (searchText === "") {
      options.value = sortedArtifacts.value.filter(
        (artifact) =>
          !props.hiddenArtifactIds?.includes(artifact.id) &&
          !hiddenTypes.value.includes(artifact.type)
      );
    } else {
      const lowercaseSearchText = searchText.toLowerCase();

      options.value = sortedArtifacts.value.filter(
        (artifact) =>
          !props.hiddenArtifactIds?.includes(artifact.id) &&
          !hiddenTypes.value.includes(artifact.type) &&
          filterArtifacts(artifact, lowercaseSearchText)
      );
    }
  });
}

/**
 * Toggles whether a type is visible in the artifact list.
 * @param option - The type to toggle.
 * @param allOptions - All possible types.
 */
function handleTypeChange(
  option: ArtifactTypeSchema,
  allOptions: ArtifactTypeSchema[]
): void {
  if (hiddenTypes.value.length === 0) {
    hiddenTypes.value = allOptions
      .map((type) => type.name)
      .filter((type) => type !== option.name);
  } else if (hiddenTypes.value.find((type) => type === option.name)) {
    hiddenTypes.value = hiddenTypes.value.filter(
      (type) => type !== option.name
    );
  } else {
    hiddenTypes.value.push(option.name);
  }

  if (hiddenTypes.value.length === allOptions.length) {
    hiddenTypes.value = [];
  }

  filterOptions("", (fn) => fn());
}

/**
 * If default hidden types are set, the artifact list will be re-filtered when opened.
 */
function handleReloadOptions(): void {
  if (!props.defaultHiddenTypes) return;

  hiddenTypes.value = props.defaultHiddenTypes;
  filterOptions("", (fn) => fn());
}

// When the model changes, filter selected options to the top.
watch(
  () => model.value,
  () => (options.value = sortSelectedArtifactsToTop(options.value, model.value))
);

return (_ctx: any,_cache: any) => {
  const _component_q_select = _resolveComponent("q-select")!

  return (_openBlock(), _createBlock(_component_q_select, {
    modelValue: _unref(model),
    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(model) ? (model).value = $event : null)),
    filled: "",
    "use-chips": "",
    "map-options": "",
    "emit-value": "",
    "use-input": "",
    clearable: _ctx.multiple,
    multiple: _ctx.multiple,
    label: _ctx.label || 'Artifact',
    options: options.value,
    "option-label": "name",
    "option-value": "id",
    "input-debounce": "0",
    onFilter: filterOptions,
    onPopupShow: handleReloadOptions
  }, {
    "before-options": _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(TypeButtons), {
          class: "width-fit q-ml-auto",
          "default-visible": "",
          "hidden-types": hiddenTypes.value,
          onClick: handleTypeChange
        }, null, 8, ["hidden-types"]),
        _renderSlot(_ctx.$slots, "before-options")
      ])
    ]),
    option: _withCtx(({ opt, itemProps }) => [
      _createVNode(ArtifactBodyDisplay, _mergeProps(itemProps, {
        "display-title": "",
        artifact: opt
      }), null, 16, ["artifact"])
    ]),
    "selected-item": _withCtx(({
        opt,
        index,
        removeAtIndex,
      }: {
        opt: ArtifactSchema;
        index: number;
        removeAtIndex(num: number): void;
      }) => [
      (!!opt && opt.name && index < 3)
        ? (_openBlock(), _createBlock(_unref(AttributeChip), {
            key: 0,
            removable: "",
            value: opt.name,
            onRemove: ($event: any) => (removeAtIndex(index))
          }, null, 8, ["value", "onRemove"]))
        : (index === 3)
          ? (_openBlock(), _createBlock(_unref(Typography), {
              key: 1,
              secondary: "",
              x: "2",
              value: '+' + (selectedCount.value - 3)
            }, null, 8, ["value"]))
          : _createCommentVNode("", true)
    ]),
    _: 3
  }, 8, ["modelValue", "clearable", "multiple", "label", "options"]))
}
}

})